import React, { useEffect, useState, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchTenants, startTestMigration } from "../app/actions";
import { withRouter } from "react-router-dom";
import { Table, Form, Card, Row, Col, Button, Alert } from "react-bootstrap"

const TenantList = ({history}) => {
    const tenants = useSelector(store => store.app.tenants) || [];
    const dispatch = useDispatch();
    
    const [selectedMigrationStatus, setSelectedMigrationStatus] = useState("pending");
    const [selectedBusinessStatuses, setSelectedBusinessStatuses] = useState(['Green', 'Yellow', 'Red']);
    const [selectedActiveValues, setSelectedActiveValues] = useState(['Yes']);
    const [bulkTestMigrating, setBulkTestMigrating] = useState(false);
    const [bulkMigrationResult, setBulkMigrationResult] = useState(null);

    useEffect(() => {
        dispatch(fetchTenants(1))
    }, [])

    // Poll for migration status updates when filter is set to "in_progress"
    useEffect(() => {
        if (selectedMigrationStatus !== 'in_progress') {
            return;
        }

        const intervalId = setInterval(() => {
            dispatch(fetchTenants(1));
        }, 10000); // Poll every 10 seconds

        return () => clearInterval(intervalId);
    }, [selectedMigrationStatus, dispatch])

    // Standard migration statuses
    const migrationStatuses = ['pending', 'in_progress', 'successful', 'failed'];
    
    // Business statuses
    const businessStatuses = ['Shut Down', 'Green', 'Yellow', 'Red'];

    const activeValues = ['Yes', 'No'];

    // Filter tenants based on selected filters
    const filteredTenants = useMemo(() => {
        return tenants.filter(tenant => {
            
            if(tenant.Type !== 'Paying Customer') {
                return false;
            }

            // Filter by MigrationTestStatus
            if (selectedMigrationStatus && tenant.MigrationTestStatus !== selectedMigrationStatus) {
                return false;
            }
            
            // Filter by BusinessStatus - INCLUDE tenants that have any of the selected statuses
            if (selectedBusinessStatuses.length === 0) {
                return false;
            }
            if (!selectedBusinessStatuses.includes(tenant.BusinessStatus)) {
                return false;
            }

            // Filter by Active - INCLUDE tenants that match any of the selected values
            if (selectedActiveValues.length === 0) {
                return false;
            }
            if (!selectedActiveValues.includes(tenant.Active)) {
                return false;
            }
            
            return true;
        });
    }, [tenants, selectedMigrationStatus, selectedBusinessStatuses, selectedActiveValues]);

    const handleMigrationStatusChange = (e) => {
        setSelectedMigrationStatus(e.target.value);
    }

    const handleBusinessStatusToggle = (businessStatus, checked) => {
        setSelectedBusinessStatuses(prev => {
            if (checked) {
                return prev.includes(businessStatus) ? prev : [...prev, businessStatus];
            }
            return prev.filter(status => status !== businessStatus);
        });
    }

    const handleActiveToggle = (activeValue, checked) => {
        setSelectedActiveValues(prev => {
            if (checked) {
                return prev.includes(activeValue) ? prev : [...prev, activeValue];
            }
            return prev.filter(value => value !== activeValue);
        });
    }

    const goToTenant = (id) => {
        history.push("/tenants/" + id);
    }

    // Get tenants eligible for test migration - exclude tenants already scheduled or in progress
    const getEligibleTenants = useMemo(() => {
        return filteredTenants.filter(tenant => 
            !['scheduled', 'in_progress', 'successful'].includes(tenant.MigrationTestStatus)
        );
    }, [filteredTenants]);

    const handleBulkTestMigration = async () => {
        const eligibleTenants = getEligibleTenants;
        
        if (eligibleTenants.length === 0) {
            setBulkMigrationResult({
                type: 'warning',
                message: 'No eligible tenants found in the filtered results for test migration. Make sure tenants are not already "scheduled" or "in_progress".'
            });
            return;
        }

        // Ask how many tenants to migrate
        const countInput = window.prompt(
            `How many tenants do you want to run test migration for?\n\n` +
            `Available: ${eligibleTenants.length} tenant(s)\n` +
            `Enter a number (1-${eligibleTenants.length}) or "all" for all tenants:`
        );

        if (countInput === null) {
            return;
        }

        let tenantsToMigrate = [];
        if (countInput.toLowerCase().trim() === 'all') {
            tenantsToMigrate = eligibleTenants;
        } else {
            const count = parseInt(countInput, 10);
            if (isNaN(count) || count < 1 || count > eligibleTenants.length) {
                setBulkMigrationResult({
                    type: 'danger',
                    message: `Invalid number. Please enter a number between 1 and ${eligibleTenants.length}, or "all".`
                });
                return;
            }
            tenantsToMigrate = eligibleTenants.slice(0, count);
        }

        const confirmMessage = `Are you sure you want to start TEST migrations for ${tenantsToMigrate.length} tenant(s)?\n\n` +
            `This will queue test migration jobs for:\n` +
            tenantsToMigrate.map(t => `- ${t.FullName} (${t.DB_NAME})`).join('\n');

        if (!window.confirm(confirmMessage)) {
            return;
        }

        // Set filter to "in_progress" to monitor migration progress
        setSelectedMigrationStatus('in_progress');
        
        setBulkTestMigrating(true);
        setBulkMigrationResult(null);
        
        const results = {
            success: [],
            failed: []
        };

        // Process test migrations sequentially with a small delay to avoid overwhelming the queue
        for (let i = 0; i < tenantsToMigrate.length; i++) {
            const tenant = tenantsToMigrate[i];
            
            try {
                await dispatch(startTestMigration(tenant.id));
                results.success.push(tenant);
            } catch (error) {
                results.failed.push({ tenant, error: error.message || 'Unknown error' });
            }

            // Small delay between requests to avoid overwhelming the system
            if (i < tenantsToMigrate.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }

        setBulkTestMigrating(false);

        // Set result message
        if (results.failed.length === 0) {
            setBulkMigrationResult({
                type: 'success',
                message: `Successfully queued test migrations for ${results.success.length} tenant(s).`
            });
        } else if (results.success.length === 0) {
            setBulkMigrationResult({
                type: 'danger',
                message: `Failed to queue test migrations for all ${results.failed.length} tenant(s). Check console for details.`
            });
        } else {
            setBulkMigrationResult({
                type: 'warning',
                message: `Queued test migrations for ${results.success.length} tenant(s). Failed for ${results.failed.length} tenant(s).`
            });
        }

        // Log failed migrations for debugging
        if (results.failed.length > 0) {
            console.error('Failed migrations:', results.failed);
        }

        // Note: Test migration statuses are updated asynchronously in MigrationTestStatus.
        dispatch(fetchTenants(1));
    }

    return <div>
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2em"}}>
            <h2>Tenant List</h2>
            <div style={{display: "flex", gap: "1em", alignItems: "center"}}>
                {getEligibleTenants.length > 0 && (
                    <div style={{fontSize: "0.875rem", color: "#6c757d"}}>
                        {getEligibleTenants.length} tenant(s) ready for test migration
                    </div>
                )}
                <Button 
                    variant="primary" 
                    onClick={handleBulkTestMigration}
                    disabled={bulkTestMigrating || getEligibleTenants.length === 0}
                >
                    {bulkTestMigrating ? 'Queuing Test Migrations...' : `Start Test Migrations (${getEligibleTenants.length})`}
                </Button>
            </div>
        </div>

        {bulkMigrationResult && (
            <Alert 
                variant={bulkMigrationResult.type} 
                dismissible 
                onClose={() => setBulkMigrationResult(null)}
                style={{marginBottom: "1em"}}
            >
                {bulkMigrationResult.message}
            </Alert>
        )}
        
        <Card style={{marginBottom: "2em"}}>
            <Card.Header>
                <h5>Filters</h5>
            </Card.Header>
            <Card.Body>
                <Row>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Migration Status</Form.Label>
                            <Form.Control
                                as="select"
                                value={selectedMigrationStatus}
                                onChange={handleMigrationStatusChange}
                            >
                                <option value="">All Statuses</option>
                                {migrationStatuses.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Include Tenants With Business Status</Form.Label>
                            <div style={{fontSize: "0.875rem", color: "#6c757d", marginBottom: "0.5rem"}}>
                                Select business statuses to include tenants that have them
                            </div>
                            <div style={{border: "1px solid #ced4da", borderRadius: "0.25rem", padding: "0.5rem"}}>
                                {businessStatuses.map(status => (
                                    <Form.Check
                                        key={status}
                                        type="checkbox"
                                        id={`business-status-${status}`}
                                        label={status}
                                        checked={selectedBusinessStatuses.includes(status)}
                                        onChange={(e) => handleBusinessStatusToggle(status, e.target.checked)}
                                    />
                                ))}
                            </div>
                            {selectedBusinessStatuses.length > 0 && (
                                <div style={{marginTop: "0.5rem", fontSize: "0.875rem", color: "#6c757d"}}>
                                    Including tenants with {selectedBusinessStatuses.length} business status(es)
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Include Tenants With Active Status</Form.Label>
                            <div style={{fontSize: "0.875rem", color: "#6c757d", marginBottom: "0.5rem"}}>
                                Select active values to include tenants that have them
                            </div>
                            <div style={{border: "1px solid #ced4da", borderRadius: "0.25rem", padding: "0.5rem"}}>
                                {activeValues.map(value => (
                                    <Form.Check
                                        key={value}
                                        type="checkbox"
                                        id={`active-${value}`}
                                        label={value}
                                        checked={selectedActiveValues.includes(value)}
                                        onChange={(e) => handleActiveToggle(value, e.target.checked)}
                                    />
                                ))}
                            </div>
                            {selectedActiveValues.length > 0 && (
                                <div style={{marginTop: "0.5rem", fontSize: "0.875rem", color: "#6c757d"}}>
                                    Including tenants with {selectedActiveValues.length} active value(s)
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>
            </Card.Body>
        </Card>

        <div style={{marginBottom: "1em"}}>
            <strong>Showing {filteredTenants.length} of {tenants.filter(tenant => (tenant.Active === 'Yes' || tenant.MigrationStatus === 'successful') && tenant.Type === 'Paying Customer' && tenant.BusinessStatus != 'Shut Down').length} tenants</strong>
        </div>

        <Table striped bordered hover>
            <thead>
                <tr>
                    <th>#</th>
                    <th>Full Name</th>
                    <th>DB_NAME</th>
                    <th>Active</th>
                    <th>Type</th>
                    <th>Migration Test Status</th>
                </tr>
            </thead>
            <tbody>
                {filteredTenants.length === 0 ? (
                    <tr>
                        <td colSpan="9" style={{textAlign: "center"}}>No tenants match the selected filters</td>
                    </tr>
                ) : (
                    filteredTenants.map(tenant => {
                        return (
                            <tr 
                                key={tenant.id} 
                                onClick={() => goToTenant(tenant.id)}
                                style={{cursor: "pointer"}}
                            >
                                <td>{tenant.id}</td>
                                <td>{tenant.FullName}</td>
                                <td>{tenant.DB_NAME}</td>
                                <td>{tenant.Active}</td>
                                <td>{tenant.Type}</td>
                                <td>
                                    <span style={{
                                        padding: "0.25rem 0.5rem",
                                        borderRadius: "0.25rem",
                                        backgroundColor: 
                                            tenant.MigrationTestStatus === "successful" ? "#d4edda" :
                                            tenant.MigrationTestStatus === "in_progress" ? "#fff3cd" :
                                            tenant.MigrationTestStatus === "pending" ? "#d1ecf1" :
                                            tenant.MigrationTestStatus === "failed" ? "#f8d7da" :
                                            "#e9ecef",
                                        color: 
                                            tenant.MigrationTestStatus === "successful" ? "#155724" :
                                            tenant.MigrationTestStatus === "in_progress" ? "#856404" :
                                            tenant.MigrationTestStatus === "pending" ? "#0c5460" :
                                            tenant.MigrationTestStatus === "failed" ? "#721c24" :
                                            "#495057"
                                    }}>
                                        {tenant.MigrationTestStatus || "N/A"}
                                    </span>
                                </td>
                            </tr>
                        );
                    })
                )}
            </tbody>
        </Table>
    </div>
} 

export default withRouter(TenantList)
